import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Helper } from '../../classes/helper.class';
import { AccMessageDialogService } from 'src/app/accountancy/custom-components/acc-message-dialog/acc-message-dialog-service/acc-message-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class HttpApiService {

  private readonly providerPath = environment.baseURL;
  private readonly hrisPath = environment.hrisURL;
  private readonly hris2Path = environment.hris2URL;

  private dialogErrorCount = 0;

  constructor(
    private http: HttpClient,
    private _msgDialog: AccMessageDialogService,
  ) {

    this.dialogErrorCount = 0;
  }

  public raw = {
    sendPutRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) => {
      const requestOptions = {
        headers: headers,
        observe: "response" as 'body',
        params: params
      };
      return this.http.put<T>(path, body, requestOptions).pipe(
        tap(values => {
          const resp = (values as HttpResponse<{ message: string, status: number }>);
          if ((resp.body?.status != null || resp.body?.status != undefined) && resp.body.status <= 0 && this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: resp.body.message ?? 'Error occurred.',
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError();
              },
            }, true);
            this.dialogErrorCount += 1;
          }
        }),
        catchError(err => {
          if (this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: Helper.handleError(err),
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(err);
              },
            }, true);
            this.dialogErrorCount += 1;
          }
          throw err;
        })
      );
    },

    sendGetRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) => {
      const requestOptions = {
        headers: headers,
        params: params,
        observe: "response" as 'body',
      };
      return this.http.get<T>(path, requestOptions).pipe(
        tap(values => {
          const resp = (values as HttpResponse<{ message: string, status: number }>);
          if ((resp.body?.status != null || resp.body?.status != undefined) && resp.body.status <= 0 && this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: resp.body.message ?? 'Error occurred.',
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(resp?.body?.message ?? 'Error occurred.');
              },
            }, true);
            this.dialogErrorCount += 1;
          }
        }),
        catchError(err => {
          if (this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: Helper.handleError(err),
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(err);
              },
            }, true);
            this.dialogErrorCount += 1;
          }
          throw err;
        })
      );
    },

    sendPostRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) => {
      const requestOptions = {
        headers: headers,
        observe: "response" as 'body',
        params: params
      };
      return this.http.post<T>(path, body, requestOptions).pipe(
        tap(values => {
          const resp = (values as HttpResponse<{ message: string, status: number }>);
          if ((resp.body?.status != null || resp.body?.status != undefined) && resp.body.status <= 0 && this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: resp.body.message ?? 'Error occurred.',
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(resp?.body?.message ?? 'Error occurred.');
              },
            }, true);
            this.dialogErrorCount += 1;
          }
        }),
        catchError(err => {
          // `ui_handled`: the caller shows its own pop-up for this error (e.g. a duplicate sales invoice reference).
          if (this.dialogErrorCount < 1 && !err?.error?.ui_handled) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: Helper.handleError(err),
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(err);
              },
            }, true);
            this.dialogErrorCount += 1;
          }
          throw err;
        })
      );
    },

    sendPatchRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) => {
      const requestOptions = {
        headers: headers,
        observe: "response" as 'body',
        params: params
      };
      return this.http.patch<T>(path, body, requestOptions).pipe(
        tap(values => {
          const resp = (values as HttpResponse<{ message: string, status: number }>);
          if ((resp.body?.status != null || resp.body?.status != undefined) && resp.body.status <= 0 && this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: resp.body.message ?? 'Error occurred.',
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(resp?.body?.message ?? 'Error occurred.');
              },
            }, true);
            this.dialogErrorCount += 1;
          }
        }),

        catchError(err => {
          if (this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: Helper.handleError(err),
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(err);
              },
            }, true);
            this.dialogErrorCount += 1;
          }
          throw err;
        })
      );
    },
    sendDeleteRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) => {
      const requestOptions = {
        headers: headers,
        observe: "response" as 'body',
        params: params
      };
      return this.http.delete<T>(path, requestOptions).pipe(
        tap(values => {
          const resp = (values as HttpResponse<{ message: string, status: number }>);
          if ((resp.body?.status != null || resp.body?.status != undefined) && resp.body.status <= 0 && this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: resp.body.message ?? 'Error occurred.',
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(resp?.body?.message ?? 'Error occurred.');
              },
            }, true);
            this.dialogErrorCount += 1;
          }
        }),
        catchError(err => {
          if (this.dialogErrorCount < 1) {
            this._msgDialog.open({
              title: 'An error occurred.',
              message: Helper.handleError(err),
              messageType: 'text-red-500',
              onClose: () => {
                this.dialogErrorCount = 0;
                if (onError) onError(err);
              },
            }, true);
            this.dialogErrorCount += 1;
          }
          throw err;
        })
      );
    },
  }



  public hris = {

    sendPutRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPutRequest<T>(`${this.hrisPath}${path}`, body, headers, params, onError),

    sendGetRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendGetRequest<T>(`${this.hrisPath}${path}`, headers, params, onError),

    sendPostRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPostRequest<T>(`${this.hrisPath}${path}`, body, headers, params, onError),

    sendPatchRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPatchRequest<T>(`${this.hrisPath}${path}`, body, headers, params, onError),

    sendDeleteRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendDeleteRequest<T>(`${this.hrisPath}${path}`, headers, params, onError),
  }

  public hris2 = {

    sendPutRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPutRequest<T>(`${this.hris2Path}${path}`, body, headers, params, onError),

    sendGetRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendGetRequest<T>(`${this.hris2Path}${path}`, headers, params, onError),

    sendPostRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPostRequest<T>(`${this.hris2Path}${path}`, body, headers, params, onError),

    sendPatchRequest: <T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendPatchRequest<T>(`${this.hris2Path}${path}`, body, headers, params, onError),

    sendDeleteRequest: <T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) =>
      this.raw.sendDeleteRequest<T>(`${this.hris2Path}${path}`, headers, params, onError),
  }

  sendGetRequest<T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) {
    return this.raw.sendGetRequest<T>(`${this.providerPath}${path}`, headers, params, onError);
  }

  sendPostRequest<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) {
    return this.raw.sendPostRequest<T>(`${this.providerPath}${path}`, body, headers, params, onError);
  }

  sendPatchRequest<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) {
    return this.raw.sendPatchRequest<T>(`${this.providerPath}${path}`, body, headers, params, onError);
  }

  sendPutRequest<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) {
    return this.raw.sendPutRequest<T>(`${this.providerPath}${path}`, body, headers, params, onError);
  }

  sendDeleteRequest<T>(path: string, headers: HttpHeaders = new HttpHeaders(), params: HttpParams = new HttpParams(), onError?: ((err?: any | null) => void) | null) {
    return this.raw.sendDeleteRequest<T>(`${this.providerPath}${path}`, headers, params, onError);
  }

}
